/**
 * AutomationRuleForm Component
 *
 * Dialog form for creating and editing automation rules.
 * Includes sections for:
 * - Basic info (name, content type)
 * - Schedule configuration
 * - Content generation config
 * - Target accounts selection
 * - Approval workflow settings
 */

import { memo, useState, useCallback } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';
import type {
  IAutomationRuleFormProps,
  IAutomationRuleFormData,
  AutomationContentType,
  AutomationRuleStatus,
  AutomationDefaultAction,
  IAutomationSchedule,
  IAutomationGenerationConfig,
  IAutomationApprovalConfig,
} from '@/@types/automation';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScheduleConfig } from './ScheduleConfig';
import { AccountSelector } from './AccountSelector';
import { TelegramConnectButton } from './TelegramConnectButton';
import { ReferencePersonSelector } from '@/components/ai-studio/ReferencePersonSelector';
import { Image, FileText, Video, Loader2, Power, Pause, FileEdit } from 'lucide-react';

/** Props for the dialog wrapper */
interface IAutomationRuleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ruleId?: Id<'automationRules'>;
  onSuccess?: () => void;
}

/** Default form data for creating new rules */
function getDefaultFormData(): IAutomationRuleFormData {
  return {
    name: '',
    contentType: 'photo',
    status: 'draft',
    targetAccountIds: [],
    schedule: {
      daysOfWeek: [1, 2, 3, 4, 5], // Weekdays default
      timeOfDay: '09:00',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    generationConfig: {
      mode: 'ai_driven',
      theme: '',
    },
    approvalConfig: {
      requireApproval: true,
      leadTimeMinutes: 60,
      defaultAction: 'skip',
    },
  };
}

/**
 * AutomationRuleForm - Dialog wrapper component
 *
 * Handles loading existing rule data and passing to internal form
 */
export const AutomationRuleForm = memo(function AutomationRuleForm({
  open,
  onOpenChange,
  ruleId,
  onSuccess,
}: IAutomationRuleFormDialogProps) {
  // Load existing rule if editing
  const existingRule = useQuery(api.automation.rules.get, ruleId ? { ruleId } : 'skip');

  // Mutations for creating and updating rules
  const createRule = useMutation(api.automation.rules.create);
  const updateRule = useMutation(api.automation.rules.update);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch Telegram connection for approval settings (in outer component to populate chatId)
  const telegramConnectionForSubmit = useQuery(api.telegram.connection.get);

  const handleSubmit = useCallback(
    async (
      data: Omit<
        IAutomationRuleFormData,
        '_id' | '_creationTime' | 'userId' | 'stats' | 'createdAt' | 'updatedAt'
      >
    ) => {
      setIsSubmitting(true);
      try {
        // Populate telegramChatId from user's connection if approval is required
        const approvalConfig = { ...data.approvalConfig };
        if (approvalConfig.requireApproval && telegramConnectionForSubmit?.telegramChatId) {
          approvalConfig.telegramChatId = telegramConnectionForSubmit.telegramChatId;
        }

        if (ruleId) {
          // Update existing rule
          await updateRule({
            ruleId,
            name: data.name,
            contentType: data.contentType,
            status: data.status,
            targetAccountIds: data.targetAccountIds,
            schedule: data.schedule,
            generationConfig: data.generationConfig,
            approvalConfig,
          });
        } else {
          // Create new rule
          await createRule({
            name: data.name,
            contentType: data.contentType,
            status: data.status,
            targetAccountIds: data.targetAccountIds,
            schedule: data.schedule,
            generationConfig: data.generationConfig,
            approvalConfig,
          });
        }
        onSuccess?.();
        onOpenChange(false);
      } catch (error) {
        console.error('Failed to save automation rule:', error);
        // TODO: Show error toast to user
      } finally {
        setIsSubmitting(false);
      }
    },
    [ruleId, createRule, updateRule, onSuccess, onOpenChange, telegramConnectionForSubmit]
  );

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{ruleId ? 'Edit Rule' : 'Create Automation Rule'}</DialogTitle>
          <DialogDescription>
            Configure automated content generation and publishing
          </DialogDescription>
        </DialogHeader>

        <AutomationRuleFormContent
          key={existingRule?._id ?? 'new'}
          rule={existingRule ?? undefined}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
        />
      </DialogContent>
    </Dialog>
  );
});

/**
 * AutomationRuleFormContent - Internal form component
 *
 * Contains the actual form fields and logic
 */
const AutomationRuleFormContent = memo(function AutomationRuleFormContent({
  rule,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: IAutomationRuleFormProps) {
  // Initialize form from rule prop (if editing) or defaults (if creating)
  const [formData, setFormData] = useState<IAutomationRuleFormData>(() => {
    if (rule) {
      return {
        name: rule.name,
        contentType: rule.contentType,
        status: rule.status,
        targetAccountIds: rule.targetAccountIds,
        schedule: rule.schedule,
        generationConfig: rule.generationConfig,
        approvalConfig: rule.approvalConfig,
      };
    }
    return getDefaultFormData();
  });

  // Fetch Telegram connection for approval settings
  const telegramConnection = useQuery(api.telegram.connection.get);

  // Handle form field updates
  const updateField = useCallback(
    <K extends keyof IAutomationRuleFormData>(field: K, value: IAutomationRuleFormData[K]) => {
      setFormData(prev => ({ ...prev, [field]: value }));
    },
    []
  );

  // Handle schedule changes
  const handleScheduleChange = useCallback((schedule: IAutomationSchedule) => {
    setFormData(prev => ({ ...prev, schedule }));
  }, []);

  // Handle account selection changes
  const handleAccountsChange = useCallback((accountIds: Id<'connectedSocialAccounts'>[]) => {
    setFormData(prev => ({ ...prev, targetAccountIds: accountIds }));
  }, []);

  // Handle generation config changes
  const updateGenerationConfig = useCallback(
    <K extends keyof IAutomationGenerationConfig>(
      field: K,
      value: IAutomationGenerationConfig[K]
    ) => {
      setFormData(prev => ({
        ...prev,
        generationConfig: { ...prev.generationConfig, [field]: value },
      }));
    },
    []
  );

  // Handle approval config changes
  const updateApprovalConfig = useCallback(
    <K extends keyof IAutomationApprovalConfig>(field: K, value: IAutomationApprovalConfig[K]) => {
      setFormData(prev => ({
        ...prev,
        approvalConfig: { ...prev.approvalConfig, [field]: value },
      }));
    },
    []
  );

  // Handle form submission
  const handleSubmit = useCallback(() => {
    onSubmit(formData);
  }, [formData, onSubmit]);

  // Validation
  const isValid =
    formData.name.trim().length > 0 &&
    formData.targetAccountIds.length > 0 &&
    formData.schedule.daysOfWeek.length > 0;

  return (
    <div className="space-y-6">
      {/* Basic Info Section */}
      <section className="space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Basic Information
        </h3>

        <div className="space-y-2">
          <Label htmlFor="rule-name">Rule Name</Label>
          <Input
            id="rule-name"
            value={formData.name}
            onChange={e => updateField('name', e.target.value)}
            placeholder="e.g., Daily Photo Post"
            maxLength={100}
          />
        </div>

        <div className="space-y-2">
          <Label>Content Type</Label>
          <RadioGroup
            value={formData.contentType}
            onValueChange={value => updateField('contentType', value as AutomationContentType)}
            className="grid grid-cols-3 gap-3"
          >
            <ContentTypeOption
              value="photo"
              label="Photo"
              icon={<Image className="h-5 w-5" />}
              enabled
            />
            <ContentTypeOption
              value="post"
              label="Text Post"
              icon={<FileText className="h-5 w-5" />}
              enabled={false}
            />
            <ContentTypeOption
              value="reel"
              label="Reel/Video"
              icon={<Video className="h-5 w-5" />}
              enabled={false}
            />
          </RadioGroup>
        </div>

        <div className="space-y-2">
          <Label>Rule Status</Label>
          <RadioGroup
            value={formData.status}
            onValueChange={value => updateField('status', value as AutomationRuleStatus)}
            className="grid grid-cols-3 gap-3"
          >
            <StatusOption
              value="draft"
              label="Draft"
              description="Not running"
              icon={<FileEdit className="h-4 w-4" />}
              isSelected={formData.status === 'draft'}
            />
            <StatusOption
              value="active"
              label="Active"
              description="Running"
              icon={<Power className="h-4 w-4" />}
              isSelected={formData.status === 'active'}
            />
            <StatusOption
              value="paused"
              label="Paused"
              description="Temporarily stopped"
              icon={<Pause className="h-4 w-4" />}
              isSelected={formData.status === 'paused'}
            />
          </RadioGroup>
          <p className="text-xs text-muted-foreground">
            Set to Active to start automated generation and publishing.
          </p>
        </div>
      </section>

      <Separator />

      {/* Schedule Section */}
      <section className="space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Schedule
        </h3>
        <ScheduleConfig schedule={formData.schedule} onChange={handleScheduleChange} />
      </section>

      <Separator />

      {/* Content Config Section */}
      <section className="space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Content Configuration
        </h3>

        {formData.contentType === 'photo' && (
          <>
            <div className="space-y-2">
              <Label>Reference Person (Optional)</Label>
              <ReferencePersonSelector
                selectedPersonId={formData.generationConfig.referencePersonId}
                onSelect={personId => updateGenerationConfig('referencePersonId', personId)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="theme">Theme / Style Guidance</Label>
              <Textarea
                id="theme"
                value={formData.generationConfig.theme ?? ''}
                onChange={e => updateGenerationConfig('theme', e.target.value)}
                placeholder="e.g., Professional headshots with modern office backgrounds, warm lighting..."
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Describe the style, mood, or theme for generated content.
              </p>
            </div>
          </>
        )}
      </section>

      <Separator />

      {/* Publish To Section */}
      <section className="space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Publish To
        </h3>
        <AccountSelector
          selectedAccountIds={formData.targetAccountIds}
          onChange={handleAccountsChange}
        />
      </section>

      <Separator />

      {/* Approval Section */}
      <section className="space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Approval Workflow
        </h3>

        <div className="space-y-4">
          <TelegramConnectButton connection={telegramConnection} />

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="require-approval">Require Approval</Label>
              <p className="text-xs text-muted-foreground">
                Get notified via Telegram before publishing
              </p>
            </div>
            <Switch
              id="require-approval"
              checked={formData.approvalConfig.requireApproval}
              onCheckedChange={checked => updateApprovalConfig('requireApproval', checked)}
            />
          </div>

          {formData.approvalConfig.requireApproval && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="lead-time">Lead Time (minutes)</Label>
                  <Input
                    id="lead-time"
                    type="number"
                    min={15}
                    max={1440}
                    value={formData.approvalConfig.leadTimeMinutes}
                    onChange={e =>
                      updateApprovalConfig('leadTimeMinutes', parseInt(e.target.value, 10) || 60)
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    How early to send approval request
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timeout-action">If No Response</Label>
                  <Select
                    value={formData.approvalConfig.defaultAction}
                    onValueChange={value =>
                      updateApprovalConfig('defaultAction', value as AutomationDefaultAction)
                    }
                  >
                    <SelectTrigger id="timeout-action">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="skip">Skip Publishing</SelectItem>
                      <SelectItem value="publish">Publish Anyway</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Action when approval times out</p>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Footer */}
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isSubmitting || !isValid}>
          {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {rule ? 'Update Rule' : 'Create Rule'}
        </Button>
      </DialogFooter>
    </div>
  );
});

/** Content type radio option component */
interface IContentTypeOptionProps {
  value: AutomationContentType;
  label: string;
  icon: React.ReactNode;
  enabled: boolean;
}

function ContentTypeOption({ value, label, icon, enabled }: IContentTypeOptionProps) {
  return (
    <Label
      htmlFor={`content-type-${value}`}
      className={cn(
        'flex flex-col items-center gap-2 rounded-lg border-2 p-4 cursor-pointer transition-colors',
        'has-[:checked]:border-primary has-[:checked]:bg-primary/5',
        !enabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      <RadioGroupItem
        id={`content-type-${value}`}
        value={value}
        disabled={!enabled}
        className="sr-only"
      />
      {icon}
      <span className="text-sm font-medium">{label}</span>
      {!enabled && <span className="text-xs text-muted-foreground">Coming soon</span>}
    </Label>
  );
}

/** Status selector option component */
interface IStatusOptionProps {
  value: AutomationRuleStatus;
  label: string;
  description: string;
  icon: React.ReactNode;
  isSelected: boolean;
}

function StatusOption({ value, label, description, icon, isSelected }: IStatusOptionProps) {
  return (
    <Label
      htmlFor={`status-${value}`}
      className={cn(
        'flex flex-col items-center gap-1 rounded-lg border-2 p-3 cursor-pointer transition-colors hover:bg-muted/50',
        isSelected && value === 'draft' && 'border-primary bg-primary/5',
        isSelected && value === 'active' && 'border-green-500 bg-green-500/10',
        isSelected && value === 'paused' && 'border-yellow-500 bg-yellow-500/10',
        !isSelected && 'border-muted'
      )}
    >
      <RadioGroupItem id={`status-${value}`} value={value} className="sr-only" />
      <span
        className={cn(
          value === 'active' && isSelected && 'text-green-600',
          value === 'paused' && isSelected && 'text-yellow-600'
        )}
      >
        {icon}
      </span>
      <span className="text-sm font-medium">{label}</span>
      <span className="text-xs text-muted-foreground">{description}</span>
    </Label>
  );
}
